import React, {
  FC,
  ImgHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import checkInViewIntersectionObserver from "utils/isInViewPortIntersectionObserver";

export interface NcImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  containerClassName?: string;
  lowQualitySrc?: string; // Optional low-quality image placeholder
}

const NcImage: FC<NcImageProps> = ({
  containerClassName = "",
  alt = "nc-imgs",
  src = "",
  className = "object-cover w-full h-full",
  lowQualitySrc,
  ...args
}) => {
  const _containerRef = useRef<HTMLDivElement>(null);
  const [imageSrc, setImageSrc] = useState<string>(lowQualitySrc || "");
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [shouldLoad, setShouldLoad] = useState<boolean>(false);

  // Set up intersection observer to detect when image is in viewport
  useEffect(() => {
    if (!_containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "200px", // Start loading 200px before it comes into view
        threshold: 0.01,
      }
    );

    observer.observe(_containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Load the image when shouldLoad becomes true
  useEffect(() => {
    if (!shouldLoad || !src) return;

    const img = new Image();
    img.src = src;
    img.decoding = "async";

    img.onload = () => {
      setImageSrc(src);
      setImageLoaded(true);
    };

    img.onerror = () => {
      console.error(`Failed to load image: ${src}`);
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [shouldLoad, src]);

  return (
    <div
      className={`nc-NcImage ${containerClassName}`}
      data-nc-id="NcImage"
      ref={_containerRef}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          className={`${className} ${
            !imageLoaded && lowQualitySrc
              ? "blur-sm scale-105"
              : "blur-0 scale-100"
          }  w-full object-cover transform-gpu will-change-transform  md:group-hover:scale-105 transition-opacity duration-700 ${
            imageLoaded ? "opacity-100" : "opacity-0"
          }`}
          alt={alt}
          loading="lazy"
          decoding="async"
          style={{
            transition: "filter 0.5s, transform 0.5s",
          }}
          {...args}
           onLoad={() => setImageLoaded(true)}
        />
      ) : (
        <div
          className={`${className} bg-neutral-200 dark:bg-neutral-600 animate-pulse`}
        >
          {/* Placeholder content */}
        </div>
      )}
    </div>
  );
};

export default NcImage;
